import isEmpty from 'lodash/isEmpty';
import { put, select, take } from 'typed-redux-saga/macro';

import { LEVER_GOLD_WEEKLY_INTRO_OFFER_ENABLED } from 'Lib/ConfigService/levers';
import { WEEK } from 'Lib/constants/time';
import { getLeverValue } from 'Lib/utils/getLeverValue';

import { getIsGoldEnabled } from 'Store/appSettings/selectors';
import { PRODUCT_TYPE_GOLD, PRODUCT_TYPE_PLATINUM } from 'Store/entities/products.schema';
import {
  clearEligibility,
  getIncentiveIntroPriceProduct,
  redeemIncentive,
} from 'Store/incentives/actions';
import {
  INCENTIVE_INTRO_PRICE_PRODUCT_SUCCESS,
  INCENTIVE_REDEEM_ERROR,
  INCENTIVE_REDEEM_SUCCESS,
} from 'Store/incentives/constants';
import {
  getIncentiveCCRequired,
  getIsIncentiveEligible,
  getIsIntroPriceIncentiveCampaign,
} from 'Store/incentives/selectors';
import { loadInsendioCampaigns } from 'Store/insendio/actions';
import { LOAD_INSENDIO_CAMPAIGNS_SUCCESS } from 'Store/insendio/constants';
import { getInsendioModal } from 'Store/insendio/selectors';
import {
  getCampaignData,
  getProductData,
  MODAL_PRESENTATION_RULE_APP_OPEN,
  MODAL_TYPE_FULL_SCREEN,
  MODAL_TYPE_PARTIAL_SCREEN,
} from 'Store/insendio/utils';
import { openModal } from 'Store/modals/actions';
import {
  MODAL_ADD_CARD,
  MODAL_CONFIRMATION,
  MODAL_GOLD_PAYWALL,
  MODAL_INCENTIVE_REDEMPTION,
  MODAL_MARKETING,
  MODAL_OFFER,
  MODAL_PLATINUM_PAYWALL,
} from 'Store/modals/constants';
import { getIsOnboardingOpen } from 'Store/onboarding/selectors';
import { connectInstagram, disconnectInstagram } from 'Store/photos/actions';
import { getShouldInstagramReauth } from 'Store/photos/selectors';
import {
  getAvailableSubOfferProduct,
  getHasPremiumSubscription,
  getOfferEligibleProductList,
  getPrimaryIntroPriceProduct,
} from 'Store/profile/revenue.selectors';
import { setGoldDeeplinkSuccess } from 'Store/user/actions';
import {
  getIncentivePromoCode,
  getIsGoldDeeplinkActive,
  getShouldShowPromoPage,
} from 'Store/user/selectors';

import {
  CONSUMABLE_PAYWALL_FROM_OFFER_MODAL_ENTRY_POINT,
  GOLD_PAYWALL_FROM_DEEPLINK,
  PAYWALL_FROM_AUTO_OPEN,
  PLATINUM_PAYWALL_FROM_AUTO_OPEN,
} from 'Store/sparks/PaywallSparks';

function* handleIncentiveModal() {
  const isIncentiveEligible = yield* select(getIsIncentiveEligible);
  const isIncentivePromoCodePresent = yield* select(getIncentivePromoCode);
  const shouldShowPromoPage = yield* select(getShouldShowPromoPage);

  if (isIncentivePromoCodePresent || shouldShowPromoPage) {
    if (isIncentiveEligible) {
      // Handle incentive eligibility check that was made pre-login
      const isIntroPriceCampaign = yield* select(getIsIntroPriceIncentiveCampaign);
      const isCCRequired = yield* select(getIncentiveCCRequired);
      if (isCCRequired) {
        // If credit card is required before redeeming, show the add cc modal
        yield* put(
          openModal(MODAL_ADD_CARD, {
            props: {
              handleCloseModal: clearEligibility(),
            },
          })
        );
      } else if (isIntroPriceCampaign) {
        // if intro price campaign, get the intro price product first
        yield* put(getIncentiveIntroPriceProduct());
        yield* take(INCENTIVE_INTRO_PRICE_PRODUCT_SUCCESS);
      } else {
        // for regular campaigns call redeem and open the success modal
        if (isIncentivePromoCodePresent) yield* put(redeemIncentive());
        yield* take([INCENTIVE_REDEEM_SUCCESS, INCENTIVE_REDEEM_ERROR]);
        yield* put(
          openModal(MODAL_INCENTIVE_REDEMPTION, {
            props: {
              handleCloseModal: clearEligibility(),
            },
          })
        );
      }
    } else {
      // show promo modal for logged in users who land on recs page
      yield* put(
        openModal(MODAL_INCENTIVE_REDEMPTION, {
          props: {
            handleCloseModal: clearEligibility(),
          },
        })
      );
    }
    return true;
  }
  return false;
}

function* openPaywallBasedOnProductType(productType, isGoldDeepLink = false) {
  if (!productType) return;

  const modalTypeMapper = {
    [PRODUCT_TYPE_GOLD]: MODAL_GOLD_PAYWALL,
    [PRODUCT_TYPE_PLATINUM]: MODAL_PLATINUM_PAYWALL,
  };

  const paywallCodeMapper = {
    [PRODUCT_TYPE_GOLD]: isGoldDeepLink ? GOLD_PAYWALL_FROM_DEEPLINK : PAYWALL_FROM_AUTO_OPEN,
    [PRODUCT_TYPE_PLATINUM]: PLATINUM_PAYWALL_FROM_AUTO_OPEN,
  };

  yield* put(
    openModal(modalTypeMapper[productType], {
      props: {
        subscriptionType: productType,
        from: paywallCodeMapper[productType],
      },
    })
  );
}

function* handleIntroPriceOrSubOfferModal() {
  const introPriceProduct = yield* select(getPrimaryIntroPriceProduct);
  const subOfferProduct = yield* select(getAvailableSubOfferProduct);

  const hasIntroPriceOffer = introPriceProduct && introPriceProduct.expiresAt === undefined;
  const hasWeeklyIntroPriceOffer = hasIntroPriceOffer && introPriceProduct?.termsUnit === WEEK;
  const hasSubOffer = subOfferProduct && subOfferProduct.expiresAt === undefined;
  const hasWeeklySubOffer = hasSubOffer && subOfferProduct?.termsUnit === WEEK;
  const hasIntroPriceOrSubOffer = hasIntroPriceOffer || hasSubOffer;
  const hasWeeklyIntroPriceOrSubOffer = hasWeeklyIntroPriceOffer || hasWeeklySubOffer;
  const isGoldWeeklyIntroOfferEnabled = getLeverValue(LEVER_GOLD_WEEKLY_INTRO_OFFER_ENABLED);

  // Open the intro price/sub offer modal only when user first received the offer
  if (hasIntroPriceOrSubOffer) {
    // Don't show the intro price/sub offer modal if the use has weekly offer and the lever is disabled
    if (hasWeeklyIntroPriceOrSubOffer && !isGoldWeeklyIntroOfferEnabled) return false;
    const product = hasIntroPriceOffer ? introPriceProduct : subOfferProduct;
    yield* openPaywallBasedOnProductType(product?.productType);
    return true;
  }
  return false;
}

function* handleGoldDeepLink() {
  const hasGoldOrPlatinum = yield* select(getHasPremiumSubscription);
  const isGoldDeeplinkActive = yield* select(getIsGoldDeeplinkActive);
  // isGoldEnabled checks for user has a gold subscription (either native or web) or has gold products
  const isGoldEnabled = yield* select(getIsGoldEnabled);
  const isEligibleForGoldPaywall = isGoldEnabled && !hasGoldOrPlatinum && !__TEST__;

  if (!isGoldDeeplinkActive) return false;

  if (isEligibleForGoldPaywall) {
    yield* openPaywallBasedOnProductType(PRODUCT_TYPE_GOLD, true);
  }
  yield* put(setGoldDeeplinkSuccess());
  return true;
}

export function* handleInsendioModal() {
  yield* put(loadInsendioCampaigns());
  yield* take(LOAD_INSENDIO_CAMPAIGNS_SUCCESS);

  const modalData = yield* select(getInsendioModal);
  if (isEmpty(modalData)) return false;

  const offerEligibleProducts = yield* select(getOfferEligibleProductList);
  const { offer, presentationRule, productType, modalType } = getCampaignData(modalData);
  const { discountedProductSku } = getProductData(offerEligibleProducts, productType);

  const offerRuleId = offer?.info?.ruleId ?? '';
  if (offerRuleId) {
    // insendio offer modals
    const productRuleId = discountedProductSku?.ruleId ?? '';
    const isOfferEligible =
      productRuleId === offerRuleId && presentationRule === MODAL_PRESENTATION_RULE_APP_OPEN;

    if (isOfferEligible) {
      yield* put(
        openModal(MODAL_OFFER, {
          props: {
            from: CONSUMABLE_PAYWALL_FROM_OFFER_MODAL_ENTRY_POINT,
          },
        })
      );
    }
    return true;
  } else if (modalType === MODAL_TYPE_FULL_SCREEN || modalType === MODAL_TYPE_PARTIAL_SCREEN) {
    // insendio marketing modals
    yield* put(openModal(MODAL_MARKETING));
    return true;
  }
  return false;
}

function* handleInstagramReauthModal() {
  const isOnboardingOpen = yield* select(getIsOnboardingOpen);
  const showInstagramReauthModal = yield* select(getShouldInstagramReauth);

  if (!__TEST__ && showInstagramReauthModal && !isOnboardingOpen) {
    yield* put(
      openModal(MODAL_CONFIRMATION, {
        props: {
          title: 'Instagram.signedOutOfInstagram',
          subtitles: 'Instagram.pleaseReconnectYourInstagram',
          acceptButton: 'continue',
          cancelButton: 'notNow',
          handleAccept: connectInstagram(),
          handleReject: disconnectInstagram(),
        },
      })
    );
    return true;
  }
  return false;
}

/**
 * Handle modals following the top-down order, continuing until a modal is opened (justified by boolean return)
 */
function* handleModalsPostLogin() {
  // BEGIN-NOSCAN
  // prettier-ignore
  switch (true) {
    case yield* handleIncentiveModal(): break;
    case yield* handleIntroPriceOrSubOfferModal(): break;
    case yield* handleGoldDeepLink(): break;
    case yield* handleInsendioModal(): break;
    case yield* handleInstagramReauthModal(): break;
    default: break;
  }
  // END-NOSCAN
}

export default handleModalsPostLogin;
